import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Building, Service } from '../models';
import { TimeUnit } from '../../pages/courses/course-instance-list/time-unit.enum';

@Injectable()
export class ServiceFormService {

  automatismsGroup: FormGroup;
  constraintsGroup: FormGroup;
  generalGroup: FormGroup;

  serviceForm: FormGroup;

  get building(): Building {
    return this._referenceBuildings.find(building => building.id === this._building.value.id);
  }

  set building(building: Building) {
    this._building.patchValue(building);
  }

  set referenceBuildings(rooms: Building[]) {
    this._referenceBuildings = rooms;
  }

  private _building: FormControl = new FormControl('', [
    Validators.required,
    this.unknownBuildingValidator(),
  ]);

  private _referenceBuildings: Building[];

  constructor(
    private formBuilder: FormBuilder,
  ) {
    this.generalGroup = this.formBuilder.group({
      building: this._building,
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: [''],
      price: ['', [Validators.required, Validators.min(0)]],
      cost: ['', [Validators.required, Validators.min(0)]],
    }, {
      validators: this.costHigherThanPriceValidator,
    });

    this.constraintsGroup = this.formBuilder.group({
      bookingHours: ['', Validators.required],
      quantityLimitAdmin: ['', [Validators.required, Validators.min(1)]],
      quantityLimitUser: ['', [Validators.required, Validators.min(1)]],
      limitBeforeBooking: ['', [Validators.required, Validators.min(0)]],
      cancellationTime: ['', [Validators.required, Validators.min(0)]],
      timeUnit: ['', [Validators.required]],
    }, {
      validators: this.limitUserHigherThanAdminValidator,
    });

    this.automatismsGroup = this.formBuilder.group({
      isAddedOnSession: ['', Validators.required],
      isAddedOnRoomBooking: ['', Validators.required],
    });

    this.serviceForm = this.formBuilder.group({
      lang: ['en', [Validators.required]],
      generalGroup: this.generalGroup,
      constraintsGroup: this.constraintsGroup,
      automatismsGroup: this.automatismsGroup,
    });
  }

  getFormFromService(service: Service): FormGroup {
    Object.keys(this.serviceForm.get('generalGroup').value).forEach(key => {
      if (key === 'building') {
        this.building = service[key] as Building;
      } else {
        this.serviceForm.get('generalGroup')['controls'][key].patchValue(service[key]);
      }
    });

    Object.keys(this.serviceForm.get('constraintsGroup').value).forEach(key => {
      if (key === 'bookingHours') {
        this.serviceForm.get('constraintsGroup')['controls'][key].patchValue([
          new Date(service.bookingStartHour),
          new Date(service.bookingEndHour),
        ]);
      } else if (key === 'cancellationTime') {
        let cancellationTime = service.billingLimit;
        let timeUnit = TimeUnit.day;
        if (service.billingLimit !== 0 && service.billingLimit % 31 === 0) {
          cancellationTime = service.billingLimit / 31;
          timeUnit = TimeUnit.month;
        }
        this.serviceForm.get('constraintsGroup')['controls'][key].patchValue(cancellationTime);
        this.serviceForm.get('constraintsGroup')['controls']['timeUnit'].patchValue(timeUnit);
      } else if (key !== 'timeUnit') {
        this.serviceForm.get('constraintsGroup')['controls'][key].patchValue(service[key]);
      }
    });

    Object.keys(this.serviceForm.get('automatismsGroup').value).forEach(key => {
      this.serviceForm.get('automatismsGroup')['controls'][key].patchValue(service[key]);
    });
    return this.serviceForm;
  }

  getServiceFromForm(form: FormGroup, service: Service = new Service()): Service {
    delete form['lang'];
    Object.keys(form).forEach(formGroup => {
      Object.keys(form[formGroup]).forEach(formControl => {
        switch (formControl) {
          case 'building':
            service['building'] = this.building['@id'];
            break;
          case 'bookingHours':
            service['bookingStartHour'] = this.getBookingHourFromRange(form[formGroup][formControl], true);
            service['bookingEndHour'] = this.getBookingHourFromRange(form[formGroup][formControl], false);
            break;
          case 'cancellationTime':
            service.billingLimit =
              // @ts-ignore
              form[formGroup][formControl] * ((form[formGroup]['timeUnit'] === TimeUnit.month) ? 31 : 1);
          break;
          default:
            service[formControl] = form[formGroup][formControl];
            break;
        }
      });
    });

    // TODO: Remove this when API is updated
    service['isActive'] = true;

    return service;
  }

  private costHigherThanPriceValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const cost = control.get('cost');
    const price = control.get('price');

    return cost && price && cost.value > price.value ? {costHigherThanPrice: true} : null;
  };


  /**
   * Returns an ISO string booking hour from booking hours range
   *
   * @param range The range that contains the booking hours
   * @param getBookingStartHour If set to true, returns bookingStartHour. Else, returns bookingEndHour
   */
  private getBookingHourFromRange(range: number[], getBookingStartHour = true): string {
    const time = getBookingStartHour === true ? range[0] : range[1];

    return new Date(time).toISOString();
  }

  private limitUserHigherThanAdminValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const user = control.get('quantityLimitUser');
    const admin = control.get('quantityLimitAdmin');

    return user && admin && user.value > admin.value ? {limitUserHigherThanAdmin: true} : null;
  };

  private unknownBuildingValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!this._referenceBuildings) {
        return null;
      }
      for (const building of this._referenceBuildings) {
        if (building.name === control?.value?.name) {
          return null;
        }
      }
      return {unknownBuilding: {value: control?.value?.name}};
    };
  }
}
